import React, { useEffect, useState } from "react";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import {
  Typography,
  TextField,
  Button,
  Box,
  MenuItem,
  FormControlLabel,
  Switch,
  Grid,
  Container,
  useTheme,
} from "@mui/material";
import CommonModal from "../modal/CommonModal";
import { tokens } from "../../../theme";

// Validation helpers
const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const isValidPhone = (phone) => {
  const phoneRegex = /^\d{10}$/;
  return phoneRegex.test(phone);
};

const AddItem = ({ fields, onAdd, onCancel }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [formState, setFormState] = useState({});
  const [errors, setErrors] = useState({});

  const getLabel = (label, isRequired) => (
    <>
      {label}
      {isRequired && (
        <span style={{ color: "red", marginLeft: 4 }}>*</span>
      )}
    </>
  );
  const requiredLabel = (text) => (
    <>
      {text} <span style={{ color: "red" }}> *</span>
    </>
  );

  const initialValue = () => {
    const defaultValues = {};

    fields.forEach((field) => {
      if (field.type === "switch") {
        defaultValues[field.name] =
          field.variant === "yesNo" ? "no" : "Active";
      }

      // DEFAULT INDIA FIX
      if (field.name === "country_id") {
        const india = field.options?.find(
          (opt) => opt.label === "India"
        );
        defaultValues[field.name] = india?.value || null;
      }

      if (field.name === "reg_open") {
        defaultValues[field.name] = 1;
      }
    });

    return defaultValues;
  };

  const [addedItem, setAddedItem] = useState(() => initialValue());
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);

  // Check if RM details should be shown based on valid_days
  const shouldShowRmDetails = () => {
    const validDaysExcluded = [1, 2, 9, 13];
    const selectedValidDays = parseInt(addedItem.valid_days);
    return !validDaysExcluded.includes(selectedValidDays) && addedItem.valid_days;
  };

  const handleFieldChange = (name, value) => {
    setAddedItem({ ...addedItem, [name]: value });

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // Validate RM fields
  const validateRmFields = () => {
    const newErrors = {};
    let isValid = true;

    if (shouldShowRmDetails()) {
      if (!addedItem.rm_name || addedItem.rm_name.trim() === "") {
        newErrors.rm_name = "RM Name is required";
        isValid = false;
      }

      if (!addedItem.rm_mobile || addedItem.rm_mobile.trim() === "") {
        newErrors.rm_mobile = "Mobile No is required";
        isValid = false;
      } else if (!isValidPhone(addedItem.rm_mobile.trim())) {
        newErrors.rm_mobile = "Phone number must be exactly 10 digits";
        isValid = false;
      }

      if (!addedItem.rm_email || addedItem.rm_email.trim() === "") {
        newErrors.rm_email = "Email is required";
        isValid = false;
      } else if (!isValidEmail(addedItem.rm_email.trim())) {
        newErrors.rm_email = "Please enter a valid email address";
        isValid = false;
      }
    }

    setErrors(newErrors);
    return isValid;
  };

  // Check if all required fields are filled
  const areAllRequiredFieldsFilled = () => {
    const optionalFields = ["institute_domain", "university_name"];

    const baseFieldsValid = fields.every((field) => {
      const value = addedItem[field.name];

      // skip optional
      if (optionalFields.includes(field.name)) return true;

      // REQUIRED SWITCH VALIDATION
      if (field.type === "switch") {
        if (field.required) {
          return value !== undefined && value !== null;
        }
        return true;
      }

      // normal fields
      return value !== undefined && value !== null && value !== "";
    });

    if (!baseFieldsValid) return false;

    // Validate RM fields with format checks
    if (!validateRmFields()) return false;

    return true;
  };

  const handleSave = () => {
    if (areAllRequiredFieldsFilled()) {
      setIsConfirmationModalOpen(true);
    }
  };

  const handleConfirmSave = async () => {
    const mappedItem = {
      ...addedItem,
    };

    if (shouldShowRmDetails()) {
      mappedItem.rm_name = addedItem.rm_name || "";
      mappedItem.rm_mobile = addedItem.rm_mobile || "";
      mappedItem.rm_email = addedItem.rm_email || "";
    }

    try {
      await onAdd(mappedItem);
      setIsConfirmationModalOpen(false);
    } catch (err) {
      console.error(err);
    }
  };

  const handleCloseModal = () => {
    setIsConfirmationModalOpen(false);
  };

  return (
    <div style={{ position: "relative", height: "100%" }}>
      <Box sx={{ overflowY: "auto", pb: 12 }}>
        <Grid container spacing={2}>
          {fields.map((field) => (
            <Grid item xs={12} xl={6} key={field.name}>
              {field.type === "switch" ? (
                <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        color={field.name === "reg_open" ? "success" : "info"}
                        checked={
                          field.name === "status"
                            ? addedItem.status === "Active"
                            : field.name === "reg_open"
                            ? addedItem.reg_open === 1
                            : false
                        }
                        onChange={(e) => {
                          if (field.name === "status") {
                            handleFieldChange(
                              "status",
                              e.target.checked ? "Active" : "InActive"
                            );
                          } else if (field.name === "reg_open") {
                            handleFieldChange("reg_open", e.target.checked ? 1 : 0);
                          }
                        }}
                      />
                    }
                    label={
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {field.name === "status"
                          ? addedItem.status === "Active"
                            ? "Active"
                            : "InActive"
                          : field.name === "reg_open"
                          ? addedItem.reg_open === 1
                            ? "Open"
                            : "Closed"
                          : ""}
                        {field.required && (
                          <span style={{ color: "red", marginLeft: 4 }}>*</span>
                        )}
                      </Typography>
                    }
                  />
                </Box>
              ) : field.type === "date" ? (
                <TextField
                  label={getLabel(field.label, field.required)}
                  required={false}
                  color="info"
                  size="small"
                  name={field.name}
                  type="date"
                  value={addedItem[field.name] || "null"}
                  onChange={(e) =>
                    handleFieldChange(field.name, e.target.value)
                  }
                  fullWidth
                  margin="dense"
                />
              ) : (
                <TextField
                  label={getLabel(field.label, field.required)}
                  required={false}
                  color="info"
                  size="small"
                  type={field.type === "number" ? "number" : "text"}
                  name={field.name}
                  value={addedItem[field.name] || ""}
                  onChange={(e) =>
                    handleFieldChange(field.name, e.target.value)
                  }
                  fullWidth
                  margin="dense"
                  select={field.type === "select"}
                >
                  {field.type === "select" &&
                    field.options.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                </TextField>
              )}
            </Grid>
          ))}
          {/* Conditional RM Details Section */}
          {shouldShowRmDetails() && (
            <Grid item xs={12}>
              <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${colors.primary[300]}` }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Relationship Manager Details
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} xl={6}>
                    <TextField
                      label={requiredLabel("Name")}
                      color="info"
                      size="small"
                      name="rm_name"
                      value={addedItem.rm_name || ""}
                      onChange={(e) =>
                        handleFieldChange("rm_name", e.target.value)
                      }
                      fullWidth
                      margin="dense"
                      error={!!errors.rm_name}
                      helperText={errors.rm_name || ""}
                    />
                  </Grid>
                  <Grid item xs={12} xl={6}>
                    <TextField
                      label={requiredLabel("Mobile No")}
                      color="info"
                      size="small"
                      name="rm_mobile"
                      type="text"
                      value={addedItem.rm_mobile || ""}
                      onChange={(e) => {
                        // Only allow digits, max 10
                        const val = e.target.value.replace(/\D/g, "").slice(0, 10);
                        handleFieldChange("rm_mobile", val);
                      }}
                      fullWidth
                      margin="dense"
                      error={!!errors.rm_mobile}
                      helperText={errors.rm_mobile || ""}
                      inputProps={{ maxLength: 10 }}
                    />
                  </Grid>
                  <Grid item xs={12} xl={6}>
                    <TextField
                      label={requiredLabel("Email")}
                      color="info"
                      size="small"
                      name="rm_email"
                      type="text"
                      autoComplete="off"
                      value={addedItem.rm_email || ""}
                      onChange={(e) =>
                        handleFieldChange("rm_email", e.target.value)
                      }
                      onFocus={(e) => e.target.select()}
                      fullWidth
                      margin="dense"
                      error={!!errors.rm_email}
                      helperText={errors.rm_email || ""}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          )}
        </Grid>
      </Box>

      <Box
        sx={{
          position: "sticky",
          bottom: 0,
          display: "flex",
          justifyContent: "flex-end",
          width: "100%",
          p: 1,
          bgcolor: "background.paper",
          borderTop: 1,
          borderColor: "divider",
          zIndex: 1,
        }}
      >
        <Button
          variant="contained"
          sx={{ mr: 1 }}
          color="info"
          onClick={handleSave}
        >
          Add
        </Button>
        <Button variant="contained" color="inherit" onClick={onCancel}>
          Cancel
        </Button>
      </Box>
      <CommonModal
        open={isConfirmationModalOpen}
        onClose={handleCloseModal}
        onConfirm={handleConfirmSave}
        action="add"
      />
    </div>
  );
};

const CommonDrawer = ({
  isOpen,
  onClose,
  fields,
  title,
  setSelectedAddItems,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const handleClose = () => {
    onClose();
  };

  return (
    <Drawer anchor="left" open={isOpen}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          bgcolor: colors.blueAccent[800],
          px: 2,
          py: 0.5,
        }}
      >
        <Typography
          variant="h6"
          color={colors.blueAccent[200]}
          fontWeight={600}
        >
          {title || ""}
        </Typography>
        <IconButton
          color="inherit"
          aria-label="Close Drawer"
          onClick={handleClose}
          edge="end"
        >
          <CloseIcon />
        </IconButton>
      </Box>
      <Container
        sx={{
          [theme.breakpoints.down("sm")]: {
            maxWidth: "600px",
          },
          [theme.breakpoints.up("sm")]: {
            width: "600px",
          },
          my: 1,
        }}
      >
        <AddItem
          title={title}
          fields={fields}
          onAdd={(addedItem) => {
            setSelectedAddItems(addedItem);
          }}
          onCancel={handleClose}
        />
      </Container>
    </Drawer>
  );
};

export default CommonDrawer;
